import {css, cx} from "@pandacss/css";
import {FC, ReactNode} from "react";

const base = css({
    fontFamily: "Inter, sans-serif",
    letterSpacing: "-1%"
})

const weights = {
    regular: css({
        fontWeight: 400
    }),
    medium: css({
        fontWeight: 500
    }),
    semiBold: css({
        fontWeight: 600
    }),
    bold: css({
        fontWeight: 700
    }),
}

const centerClass = css({
    textAlign: "center"
})

interface TextRole {
    "1": string;
    "2": string;
    "3": string;
    "4": string;
    "5"?: string;
}

type TextRoleName = 'heading' | 'body' | 'description'
type TextSizeName = "1" | "2" | "3" | "4" | "5";
type TextWeightName = 'regular' | 'medium' | 'semiBold' | 'bold';

const roles: Record<TextRoleName, TextRole> = {
    heading: {
        "1": css({
            fontSize: "32px",
            lineHeight: "36px"
        }),
        "2": css({
            fontSize: "28px",
            lineHeight: "32px"
        }),
        "3": css({
            fontSize: "24px",
            lineHeight: "28px"
        }),
        "4": css({
            fontSize: "20px",
            lineHeight: "24px"
        }),
        "5": css({
            fontSize: "18px",
            lineHeight: "20px"
        })
    },
    body: {
        "1": css({
            fontSize: "16px",
            lineHeight: "20px"
        }),
        "2": css({
            fontSize: "14px",
            lineHeight: "20px"
        }),
        "3": css({
            fontSize: "12px",
            lineHeight: "12px"
        }),
        "4": css({
            fontSize: "10px",
            lineHeight: "12px"
        }),
    },
    description: {
        "1": css({
            fontSize: "16px",
            lineHeight: "20px"
        }),
        "2": css({
            fontSize: "14px",
            lineHeight: "16px"
        }),
        "3": css({
            fontSize: "12px",
            lineHeight: "16px"
        }),
        "4": css({
            fontSize: "10px",
            lineHeight: "12px"
        })
    }
}

const textSpan = css({
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
})

const overflowTruncate = css({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
});

export const font = (role: TextRoleName, size: TextSizeName, weight: TextWeightName, center?: boolean): string => {
    return cx(base, roles[role][size], weights[weight], center ? centerClass : undefined);
}

interface TextProps {
    role?: TextRoleName;
    size?: TextSizeName;
    weight?: TextWeightName;
    children: ReactNode;
    center?: boolean;
    vcenter?: boolean;
    overflow?: 'wrap' | 'truncate'
}

export const Text: FC<TextProps> = ({role, size, weight, center, children, vcenter, overflow = 'wrap'}) => {
    const className = font(role ?? "body", size ?? "1", weight ?? "regular", center);
    return <span className={cx(vcenter ? textSpan : undefined, className, overflow === 'truncate' ? overflowTruncate : undefined)}>{children}</span>;
}

